import { supabase } from '../supabaseClient'

// Workout coach: data fetch + prompt builder. Lives in the app so
// prompt iteration ships through the normal frontend build — no
// edge function redeploy needed.
//
// fetchWorkoutContext runs against supabase-js with the user's JWT,
// so RLS still scopes the read to their own rows. buildWorkoutCoachPrompt
// is pure (just string assembly), keeps the prompt source readable
// next to the data shape it consumes.

const WINDOW_DAYS = 30

export async function fetchWorkoutContext(userId) {
  const since = new Date(Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10)

  // Latest sessions (deep joins for the per-session line) + a single
  // ancient row to tell "new logger" apart from "long-time logger with
  // a sparse recent stretch". Two round-trips in parallel via
  // Promise.all so wall time stays small.
  const [sessionsRes, earliestRes] = await Promise.all([
    supabase
      .from('sessions')
      .select(`
        id, date, session_type, duration_min, body_weight_kg, energy_rating, notes,
        exercise_logs(
          order_index, notes,
          exercise:exercises(name, muscle_group),
          sets(set_number, weight_kg, reps, rpe, is_warmup, is_failure)
        ),
        cardio_logs(cardio_type, duration_min, calories_estimated)
      `)
      .eq('user_id', userId)
      .gte('date', since)
      .order('date', { ascending: false }),
    supabase
      .from('sessions')
      .select('date')
      .eq('user_id', userId)
      .order('date', { ascending: true })
      .limit(1),
  ])
  if (sessionsRes.error) throw sessionsRes.error

  return {
    sessions: sessionsRes.data || [],
    since,
    earliestDate: earliestRes?.data?.[0]?.date || null,
  }
}

function summarise(context) {
  const lines = []
  let totalWorkSets = 0
  let totalVolume = 0
  let totalCardioMin = 0
  const exerciseFreq = {}

  for (const s of context.sessions) {
    let setsThisSession = 0
    let volThisSession = 0
    const topExercises = []
    for (const log of s.exercise_logs || []) {
      let exSets = 0
      let topWeight = 0
      for (const set of log.sets || []) {
        if (set.is_warmup) continue
        exSets += 1
        setsThisSession += 1
        const w = Number(set.weight_kg) || 0
        const r = Number(set.reps) || 0
        volThisSession += w * r
        if (w > topWeight) topWeight = w
      }
      const name = log.exercise?.name || 'unknown'
      if (exSets > 0) {
        topExercises.push({ name, sets: exSets, topWeight })
        const slot = exerciseFreq[name] || { sets: 0, volume: 0 }
        slot.sets += exSets
        slot.volume += volThisSession
        exerciseFreq[name] = slot
      }
    }
    const cardioMin = (s.cardio_logs || []).reduce(
      (sum, c) => sum + (Number(c.duration_min) || 0),
      0,
    )
    totalWorkSets += setsThisSession
    totalVolume += volThisSession
    totalCardioMin += cardioMin

    lines.push(
      `- ${s.date} | ${s.session_type || 'session'} | ${setsThisSession} work sets`
      + ` | ${Math.round(volThisSession)} kg vol`
      + (cardioMin > 0 ? ` | ${cardioMin} min cardio` : '')
      + (s.body_weight_kg ? ` | bw ${s.body_weight_kg}` : '')
      + (s.energy_rating ? ` | E ${s.energy_rating}/5` : '')
      + (topExercises.length
        ? ` | top: ${topExercises.slice(0, 3).map((e) => `${e.name} ${e.sets}×@${e.topWeight}`).join(', ')}`
        : ''),
    )
  }

  const topFreqExercises = Object.entries(exerciseFreq)
    .sort((a, b) => b[1].sets - a[1].sets)
    .slice(0, 6)
    .map(([n, v]) => `${n} (${v.sets} sets, ${Math.round(v.volume)} kg)`)

  // Real data span — oldest session in the window through today.
  // "30 days" is the bound we fetch with, not necessarily what the
  // user has actually logged.
  const dates = context.sessions.map((s) => s.date).filter(Boolean).sort()
  const oldestInWindow = dates[0] || null
  const today = new Date().toISOString().slice(0, 10)
  let spanDays = 0
  if (oldestInWindow) {
    spanDays = Math.max(
      1,
      Math.round((Date.parse(today) - Date.parse(oldestInWindow)) / 86400000) + 1,
    )
  }
  const userIsNew =
    !context.earliestDate || context.earliestDate >= context.since
  const windowLabel = userIsNew && spanDays > 0
    ? `${spanDays} days (since first session ${oldestInWindow})`
    : `last 30 days`

  return {
    headline: `Data window: ${windowLabel}. Sessions: ${context.sessions.length}.`
      + ` Total work sets: ${totalWorkSets}. Total volume: ${Math.round(totalVolume)} kg.`
      + ` Cardio: ${totalCardioMin} min.`,
    perSession: lines.join('\n'),
    topExercises: topFreqExercises.join('\n  '),
    userIsNew,
    spanDays,
  }
}

export function buildWorkoutCoachPrompt(context, language = 'en') {
  const summary = summarise(context)
  const langLine = language === 'ar'
    ? 'Respond in Egyptian Arabic. Keep it warm but direct, not preachy.'
    : 'Respond in English. Direct, no fluff.'

  // Frame appropriately for the actual data span. "Volume drift"
  // and month-over-month comparisons only make sense once there's
  // enough data; for a freshly-started log we want baseline-setting
  // observations instead.
  const framing = summary.userIsNew && summary.spanDays < 14
    ? `The user just started logging — only ${summary.spanDays} days of data. Do NOT frame this as a monthly review or talk about "the last month". Talk about what's been logged so far and what to keep an eye on as they build a baseline.`
    : `Frame this as a check-in on the user's recent training, scoped to the data window above.`

  return `You are a strength training coach analysing one person's training log. ${langLine}

${framing}

DATA:
${summary.headline}

PER-SESSION SUMMARY (newest first):
${summary.perSession || '(no sessions)'}

TOP EXERCISES BY FREQUENCY:
  ${summary.topExercises || '(none)'}

YOUR TASK
Write a short coach's note (≤180 words) covering:
1. What's going well (consistency, progressive overload signs, balance).
2. One specific weakness or risk you see (volume drift, missing muscle groups, skipped cardio).
3. ONE concrete suggestion for the NEXT session — exercise, set/rep, or focus.

RULES:
- This is a one-shot output, NOT a conversation. Do NOT ask the user any questions. Do NOT invite a reply. Do NOT close with "show me your next session" or similar. End with the suggestion and stop.
- Avoid generic advice. Reference specific exercises or numbers from the data above.
- If the data is too thin to say something specific, say so plainly in one sentence and then still give the one concrete suggestion based on what IS there. Do not ask the user what they want.`
}
